import React from "react"
import PropTypes from "prop-types"
import styled, { keyframes, css } from "styled-components"

/**
 * VaultBridge — Stacked Plates symbol (Round 03, Variant 01: Offset · 3 plates)
 * Geometry locked from the design canvas:
 *   upper:  x=6,  y=14, w=44, h=10
 *   middle: x=14, y=27, w=44, h=10  (accent)
 *   lower:  x=6,  y=40, w=44, h=10
 * Stagger offset = 8 units, plate gap = 3 units, viewBox 64×64.
 */
export const Symbol = ({ size = 64, ink, accent, mono = false, title = "VaultBridge" }) => {
  const middle = mono ? ink : accent
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      role="img"
      aria-label={title}
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>{title}</title>
      <rect x="6"  y="14" width="44" height="10" fill={ink} />
      <rect x="14" y="27" width="44" height="10" fill={middle} />
      <rect x="6"  y="40" width="44" height="10" fill={ink} />
    </svg>
  )
}

Symbol.propTypes = {
  size: PropTypes.number,
  ink: PropTypes.string.isRequired,
  accent: PropTypes.string.isRequired,
  mono: PropTypes.bool,
  title: PropTypes.string,
}

/* ───── Animated reveal: top → bottom → middle (accent) ───── */

const plateIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const plateAnim = css`
  transform-box: fill-box;
  transform-origin: center;
  opacity: 0;
  animation: ${plateIn} 360ms cubic-bezier(0.2, 0.7, 0.2, 1) forwards;
  animation-delay: var(--delay, 0ms);

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 1;
  }
`

const PlateTop    = styled.rect`${plateAnim}`
const PlateBottom = styled.rect`${plateAnim}`
const PlateMiddle = styled.rect`${plateAnim}`

export const AnimatedSymbol = ({
  size = 64,
  ink,
  accent,
  mono = false,
  title = "VaultBridge",
}) => {
  const middle = mono ? ink : accent
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      role="img"
      aria-label={title}
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>{title}</title>
      <PlateTop    x="6"  y="14" width="44" height="10" fill={ink}    style={{ "--delay": "0ms"   }} />
      <PlateBottom x="6"  y="40" width="44" height="10" fill={ink}    style={{ "--delay": "180ms" }} />
      <PlateMiddle x="14" y="27" width="44" height="10" fill={middle} style={{ "--delay": "360ms" }} />
    </svg>
  )
}

AnimatedSymbol.propTypes = {
  size: PropTypes.number,
  ink: PropTypes.string.isRequired,
  accent: PropTypes.string.isRequired,
  mono: PropTypes.bool,
  title: PropTypes.string,
}

const WordmarkText = styled.span`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-weight: 700;
  font-size: ${({ $height }) => `${$height}px`};
  letter-spacing: -0.025em;
  color: ${({ $ink }) => $ink};
  line-height: 1;
  display: inline-flex;

  span:last-child {
    font-weight: 400;
  }
`

export const Wordmark = ({ height = 38, ink }) => (
  <WordmarkText $height={height} $ink={ink}>
    <span>Vault</span>
    <span>Bridge</span>
  </WordmarkText>
)

Wordmark.propTypes = {
  height: PropTypes.number,
  ink: PropTypes.string.isRequired,
}

const LockupRoot = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ $gap }) => $gap}px;
`

const LockupText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const Descriptor = styled.span`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 10px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${({ $ink }) => $ink};
  opacity: 0.7;
  font-weight: 500;
`

/**
 * Horizontal lockup: symbol + wordmark + (optional) descriptor.
 */
export const Lockup = ({
  size = 64,
  height = 36,
  ink,
  accent,
  mono = false,
  descriptor = true,
  gap = 20,
}) => (
  <LockupRoot $gap={gap}>
    <Symbol size={size} ink={ink} accent={accent} mono={mono} />
    <LockupText>
      <Wordmark height={height} ink={ink} />
      {descriptor && <Descriptor $ink={ink}>POWERED BY MEI PROXY</Descriptor>}
    </LockupText>
  </LockupRoot>
)

Lockup.propTypes = {
  size: PropTypes.number,
  height: PropTypes.number,
  ink: PropTypes.string.isRequired,
  accent: PropTypes.string.isRequired,
  mono: PropTypes.bool,
  descriptor: PropTypes.bool,
  gap: PropTypes.number,
}

const StackedRoot = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
`

export const Stacked = ({ size = 88, height = 34, ink, accent, mono = false }) => (
  <StackedRoot>
    <Symbol size={size} ink={ink} accent={accent} mono={mono} />
    <Wordmark height={height} ink={ink} />
    <Descriptor $ink={ink}>POWERED BY MEI PROXY</Descriptor>
  </StackedRoot>
)

Stacked.propTypes = {
  size: PropTypes.number,
  height: PropTypes.number,
  ink: PropTypes.string.isRequired,
  accent: PropTypes.string.isRequired,
  mono: PropTypes.bool,
}

export default Lockup
